import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home } from "lucide-react";
import { loginWithGoogle } from "../lib/firebase";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const redirectTo = new URLSearchParams(location.search).get("redirect") || "/";

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      navigate(redirectTo);
    } catch (error) {
      console.error(error);
      toast.error("Failed to log in with Google");
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.14),_transparent_28%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-8 font-sans">
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(15,23,42,0.03)_0%,transparent_35%,rgba(16,185,129,0.05)_100%)]" />

      <div className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/10 lg:grid-cols-[1.1fr_.9fr]">
        <div className="flex flex-col justify-between gap-10 bg-slate-950 px-8 py-10 text-white sm:px-10 lg:px-12">
          <div className="space-y-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-white shadow-sm">
                <Home className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">LandMiner CRM</p>
                <p className="mt-1 text-sm text-slate-300">A structured workspace for deal flow.</p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-300">Command center</p>
              <h1 className="max-w-lg text-4xl font-semibold tracking-tight sm:text-5xl">
                Move from signal to lead without losing the thread.
              </h1>
              <p className="max-w-xl text-base leading-7 text-slate-300">
                See what matters first, work your follow-up queue, and keep every step of the pipeline easy to understand.
              </p>
            </div>
          </div>

          <div className="grid gap-3 text-sm text-slate-300 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">01</p>
              <p className="mt-2 font-medium text-white">Spot the signal</p>
              <p className="mt-1 leading-6">Review public distress clues and score the best opportunities first.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">02</p>
              <p className="mt-2 font-medium text-white">Qualify the lead</p>
              <p className="mt-1 leading-6">Move the right address into your inbox and add the next action.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">03</p>
              <p className="mt-2 font-medium text-white">Keep momentum</p>
              <p className="mt-1 leading-6">Work the follow-up queue and close deals with less friction.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-center gap-8 px-6 py-10 sm:px-10 lg:px-12">
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Sign in</p>
            <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Welcome back</h2>
            <p className="max-w-md text-sm leading-6 text-slate-500">
              Sign in to access your leads, pipeline, follow-up queue, and the rest of your workspace.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-slate-950 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:opacity-50"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {loading ? "Signing in..." : "Continue with Google"}
            </button>

            <div className="mt-4 grid gap-3 text-sm text-slate-500 sm:grid-cols-2">
              <button type="button" onClick={() => navigate("/pricing")} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left font-medium text-blue-600 transition-colors hover:border-slate-300 hover:bg-slate-50">
                View pricing
              </button>
              <button type="button" onClick={() => navigate("/unsubscribe")} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-left font-medium text-blue-600 transition-colors hover:border-slate-300 hover:bg-slate-50">
                Unsubscribe / manage billing
              </button>
            </div>

            <p className="mt-4 text-center text-xs leading-5 text-slate-400">
              Compare Free, Foundation, Operator, and Atlas after signing in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
